/* @jsx h */
import { computed, defineComponent, h } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { RouterLink } from "vue-router";
import { useLocalization } from "@lemmonhq/uikit";
import { useTrpc } from "@app/vue/providers/index";

/** Sent as the design id when the merchant keeps the plain default. */
const PLAIN = "";

export type WalletDesignSelectProps = {
  kind: "membership" | "coin";
  scope?: "shop" | "group";
  /** The selected design id; null while creating with the plain default. */
  value: string | null;
  /** True for a new program, which may keep the plain default. */
  creating: boolean;
  onChange: (id: string | null) => void;
};

/**
 * Chooses the wallet pass design a membership or coin program issues.
 *
 * Lists every design of the program's kind for the owner. A new program can
 * keep "Plain pass (default)": the API then creates a design from the bundled
 * default in the same transaction, so saving is never blocked for lack of a
 * design. An existing program always has one and can only switch to another.
 */
export const WalletDesignSelect = defineComponent<WalletDesignSelectProps>({
  name: "WalletDesignSelect",
  props: ["kind", "scope", "value", "creating", "onChange"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();

    const designsQuery = useQuery({
      queryKey: ["walletDesigns", props.scope ?? "shop", props.kind],
      queryFn: () =>
        props.scope === "group"
          ? trpc.walletDesigns.groupList.query({ kind: props.kind })
          : trpc.walletDesigns.list.query({ kind: props.kind }),
    });
    const designs = computed(() => designsQuery.data.value ?? []);
    const designsPath = computed(() =>
      props.scope === "group" ? "/group/loyalty/wallet" : "/manager/loyalty/wallet",
    );

    return () => (
      <div class="form-control">
        <label class="label">
          <span class="label-text text-accent font-medium">
            {t("promotions.wallet.program_design")}
          </span>
        </label>
        <div class="flex items-center gap-2">
          <select
            class="select select-bordered focus:select-primary flex-1 min-w-0"
            value={props.value ?? PLAIN}
            disabled={designsQuery.isLoading.value}
            onChange={(event) => {
              const id = (event.target as HTMLSelectElement).value;
              props.onChange(id === PLAIN ? null : id);
            }}
          >
            {props.creating && (
              <option value={PLAIN}>{t("promotions.wallet.plain_design")}</option>
            )}
            {!props.creating && !props.value && (
              <option value={PLAIN} disabled>
                {t("promotions.wallet.choose_design")}
              </option>
            )}
            {designs.value.map((design) => (
              <option key={design.id} value={design.id}>
                {design.name}
              </option>
            ))}
          </select>
          <RouterLink
            class="btn btn-sm btn-ghost shrink-0"
            to={
              props.value
                ? { path: designsPath.value, query: { design: props.value } }
                : { path: designsPath.value, query: { new: props.kind } }
            }
          >
            {props.value
              ? t("promotions.wallet.edit_design")
              : t("promotions.wallet.new_design")}
          </RouterLink>
        </div>
        <label class="label">
          <span class="label-text-alt text-base-content/60">
            {t("promotions.wallet.program_design_hint")}
          </span>
        </label>
      </div>
    );
  },
});
